import React, { useState, useEffect } from 'react';
import { 
  User, Phone, Mail, Tag, FileText, Loader2, CheckCircle,
  MessageSquare, DollarSign, Clock, Package, AlertTriangle,
  MessageCircle, Users, Send, Eye, PieChart, TrendingUp,
  Palette, UserPlus, GitBranch, Filter, BarChart3, Building2,
  Percent, Calendar, Download, Settings, Link2, Star, Globe, Zap
} from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import {
  WizardModal,
  FormField,
  TextInput,
  TextArea,
  SelectInput,
  AdvancedSection,
  ContactSelector,
  ConfirmationBanner,
  getAuthHeaders
} from './AdvancedCRMWizards';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════
// J) CONVERSATIONS / TEAM COMMUNICATION
// ═══════════════════════════════════════════════════════════════

// 9) Send Internal Team Message Wizard
export function SendInternalMessageWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    teamMemberId: initialData.teamMemberId || '',
    subject: initialData.subject || '',
    message: initialData.message || '',
    attachLink: initialData.attachLink || '',
  });

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const response = await fetch(`${API_URL}/api/team/members`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setTeamMembers(data || []);
        }
      } catch (error) {
        console.error('Error fetching team members:', error);
      } finally {
        setLoading(false);
      }
    };
    if (isOpen) fetchTeamMembers();
  }, [isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.teamMemberId) {
      toast.error('Please select a team member');
      return;
    }
    if (!formData.message.trim()) {
      toast.error('Message is required');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/internal-message`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          team_member_id: formData.teamMemberId,
          subject: formData.subject || undefined,
          message: formData.message,
          attach_link: formData.attachLink || undefined,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success('Message sent!');
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to send message');
      }
    } catch (error) {
      toast.error('Error sending message: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Send Internal Message"
      subtitle="Message a team member"
      icon={Send}
      iconColor="bg-blue-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting || loading}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Message
            </Button>
          </div>
        </>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : (
        <>
          <FormField label="Team Member" required>
            <SelectInput
              value={formData.teamMemberId}
              onChange={v => handleChange('teamMemberId', v)}
              placeholder="Select team member"
              options={teamMembers.map(m => ({ value: m.id, label: m.name || m.email }))}
            />
          </FormField>

          <FormField label="Subject (optional)">
            <TextInput
              value={formData.subject}
              onChange={v => handleChange('subject', v)}
              placeholder="Message subject"
            />
          </FormField>

          <FormField label="Message" required>
            <TextArea
              value={formData.message}
              onChange={v => handleChange('message', v)}
              placeholder="Type your message..."
              rows={4}
            />
          </FormField>

          <FormField label="Attach Link (optional)">
            <TextInput
              icon={Link2}
              value={formData.attachLink}
              onChange={v => handleChange('attachLink', v)}
              placeholder="https://..."
            />
          </FormField>
        </>
      )}
    </WizardModal>
  );
}

// 10) Check Unread Messages Wizard
export function CheckUnreadMessagesWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [formData, setFormData] = useState({
    filter: initialData.filter || 'all',
    timeRange: initialData.timeRange || '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (formData.filter !== 'all') params.append('channel', formData.filter);
      if (formData.timeRange) params.append('hours', formData.timeRange);

      const response = await fetch(`${API_URL}/api/wizards/crm/unread-messages?${params}`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      }
    } catch (error) {
      toast.error('Error fetching messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) fetchMessages();
  }, [isOpen]);

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Check Unread Messages"
      subtitle="View unread conversations"
      icon={Eye}
      iconColor="bg-blue-500/20"
      size="large"
      footer={
        <>
          <div className="text-sm text-gray-400">
            {messages.length} unread message{messages.length !== 1 ? 's' : ''}
          </div>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </>
      }
    >
      <div className="flex gap-4 mb-4">
        <FormField label="Filter by Channel">
          <SelectInput
            value={formData.filter}
            onChange={v => {
              handleChange('filter', v);
              setTimeout(fetchMessages, 100);
            }}
            options={[
              { value: 'all', label: 'All Channels' },
              { value: 'sms', label: 'SMS' },
              { value: 'email', label: 'Email' },
              { value: 'facebook', label: 'Facebook' },
            ]}
          />
        </FormField>
        <FormField label="Time Range">
          <SelectInput
            value={formData.timeRange}
            onChange={v => {
              handleChange('timeRange', v);
              setTimeout(fetchMessages, 100);
            }}
            placeholder="All time"
            options={[
              { value: '24', label: 'Last 24 hours' },
              { value: '48', label: 'Last 48 hours' },
              { value: '168', label: 'Last 7 days' },
            ]}
          />
        </FormField>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : messages.length > 0 ? (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {messages.map(msg => (
            <div key={msg.id} className="p-3 bg-white/5 rounded-xl border border-white/10">
              <div className="flex items-center gap-3">
                <div className={cn(
                  "w-8 h-8 rounded-lg flex items-center justify-center",
                  msg.channel === 'sms' ? "bg-green-500/20" :
                  msg.channel === 'email' ? "bg-blue-500/20" : "bg-purple-500/20"
                )}>
                  {msg.channel === 'sms' ? <MessageSquare className="w-4 h-4 text-green-400" /> :
                   msg.channel === 'email' ? <Mail className="w-4 h-4 text-blue-400" /> :
                   <MessageCircle className="w-4 h-4 text-purple-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-medium truncate">{msg.contact_name}</p>
                  <p className="text-xs text-gray-400 truncate">{msg.preview}</p>
                </div>
                <span className="text-xs text-gray-500">{msg.time_ago}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-400" />
          <p className="text-gray-400">No unread messages</p>
        </div>
      )}
    </WizardModal>
  );
}

// 11) Assign Conversation Wizard
export function AssignConversationWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    assignToId: initialData.assignToId || '',
  });

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const response = await fetch(`${API_URL}/api/team/members`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setTeamMembers(data || []);
        }
      } catch (error) {
        console.error('Error fetching team members:', error);
      } finally {
        setLoading(false);
      }
    };
    if (isOpen) fetchTeamMembers();
  }, [isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!selectedContact) {
      toast.error('Please select a contact');
      return;
    }
    if (!formData.assignToId) {
      toast.error('Please select a team member to assign');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/assign-conversation`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          contact_id: selectedContact.id,
          assign_to_id: formData.assignToId,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success('Conversation assigned!');
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to assign conversation');
      }
    } catch (error) {
      toast.error('Error assigning conversation: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Assign Conversation"
      subtitle="Assign a conversation to a team member"
      icon={Users}
      iconColor="bg-blue-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting || loading}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
              Assign
            </Button>
          </div>
        </>
      }
    >
      <ContactSelector
        selectedContact={selectedContact}
        onSelect={setSelectedContact}
        label="Select Contact/Conversation"
      />

      <FormField label="Assign To" required>
        <SelectInput
          value={formData.assignToId}
          onChange={v => handleChange('assignToId', v)}
          placeholder="Select team member"
          options={teamMembers.map(m => ({ value: m.id, label: m.name || m.email }))}
        />
      </FormField>
    </WizardModal>
  );
}

// ═══════════════════════════════════════════════════════════════
// K) REPORTING / DASHBOARDS
// ═══════════════════════════════════════════════════════════════

// 12) Dashboard Snapshot Report Wizard
export function DashboardSnapshotWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [formData, setFormData] = useState({
    dateRange: initialData.dateRange || '30',
    metrics: initialData.metrics || ['leads', 'opportunities', 'revenue'],
    exportFormat: initialData.exportFormat || 'summary',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleMetric = (metric) => {
    const current = formData.metrics;
    if (current.includes(metric)) {
      handleChange('metrics', current.filter(m => m !== metric));
    } else {
      handleChange('metrics', [...current, metric]);
    }
  };

  const generateReport = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/dashboard-report`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          date_range_days: parseInt(formData.dateRange),
          metrics: formData.metrics,
          export_format: formData.exportFormat,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        setReport(result.report);
        toast.success('Report generated!');
        onSuccess?.(result);
      } else {
        toast.error(result.error || 'Failed to generate report');
      }
    } catch (error) {
      toast.error('Error generating report: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Dashboard Snapshot"
      subtitle="Get a quick metrics overview"
      icon={PieChart}
      iconColor="bg-cyan-500/20"
      size="large"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Close</Button>
            <Button onClick={generateReport} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PieChart className="w-4 h-4 mr-2" />}
              Generate Report
            </Button>
          </div>
        </>
      }
    >
      <FormField label="Date Range" required>
        <SelectInput
          value={formData.dateRange}
          onChange={v => handleChange('dateRange', v)}
          options={[
            { value: '7', label: 'Last 7 days' },
            { value: '30', label: 'Last 30 days' },
            { value: '90', label: 'Last 90 days' },
            { value: '365', label: 'Last year' },
          ]}
        />
      </FormField>

      <FormField label="Metrics to Include">
        <div className="grid grid-cols-3 gap-2">
          {[
            { value: 'leads', label: 'Leads' },
            { value: 'opportunities', label: 'Opportunities' },
            { value: 'revenue', label: 'Revenue' },
            { value: 'conversion_rate', label: 'Conversion Rate' },
            { value: 'sms_sent', label: 'SMS Sent' },
            { value: 'email_open_rate', label: 'Email Open Rate' },
          ].map(opt => (
            <label
              key={opt.value}
              className={cn(
                "p-2 rounded-lg border cursor-pointer transition-colors text-center",
                formData.metrics.includes(opt.value)
                  ? "bg-cyan-500/20 border-cyan-500/50 text-cyan-400"
                  : "bg-white/5 border-white/10 text-gray-400"
              )}
            >
              <input
                type="checkbox"
                checked={formData.metrics.includes(opt.value)}
                onChange={() => toggleMetric(opt.value)}
                className="sr-only"
              />
              <span className="text-xs">{opt.label}</span>
            </label>
          ))}
        </div>
      </FormField>

      <FormField label="Export Format">
        <SelectInput
          value={formData.exportFormat}
          onChange={v => handleChange('exportFormat', v)}
          options={[
            { value: 'summary', label: 'Summary in Chat' },
            { value: 'pdf', label: 'PDF' },
            { value: 'csv', label: 'CSV' },
          ]}
        />
      </FormField>

      {report && (
        <div className="mt-4 p-4 bg-white/5 rounded-xl border border-white/10">
          <h4 className="text-sm font-medium text-white mb-3">Report Results</h4>
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(report).map(([key, value]) => (
              <div key={key} className="p-3 bg-white/5 rounded-lg">
                <p className="text-xs text-gray-400 capitalize">{key.replace('_', ' ')}</p>
                <p className="text-lg font-semibold text-white">
                  {typeof value === 'number' && key.includes('revenue') ? `$${value.toLocaleString()}` :
                   typeof value === 'number' && key.includes('rate') ? `${value}%` :
                   value?.toLocaleString?.() || value}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </WizardModal>
  );
}

// 13) Revenue Report Wizard
export function RevenueReportWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [pipelines, setPipelines] = useState([]);
  const [formData, setFormData] = useState({
    dateRange: initialData.dateRange || '30',
    pipelineId: initialData.pipelineId || '',
    stageBreakdown: initialData.stageBreakdown ?? true,
  });

  useEffect(() => {
    const fetchPipelines = async () => {
      try {
        const response = await fetch(`${API_URL}/api/wizards/crm/pipelines`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setPipelines(data.pipelines || []);
        }
      } catch (error) {
        console.error('Error fetching pipelines:', error);
      }
    };
    if (isOpen) fetchPipelines();
  }, [isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const generateReport = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/revenue-report`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          date_range_days: parseInt(formData.dateRange),
          pipeline_id: formData.pipelineId || undefined,
          stage_breakdown: formData.stageBreakdown,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        setReport(result.report);
        toast.success('Revenue report generated!');
        onSuccess?.(result);
      } else {
        toast.error(result.error || 'Failed to generate report');
      }
    } catch (error) {
      toast.error('Error generating report: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Revenue Report"
      subtitle="View revenue analytics"
      icon={TrendingUp}
      iconColor="bg-cyan-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Close</Button>
            <Button onClick={generateReport} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TrendingUp className="w-4 h-4 mr-2" />}
              Generate Report
            </Button>
          </div>
        </>
      }
    >
      <FormField label="Date Range" required>
        <SelectInput
          value={formData.dateRange}
          onChange={v => handleChange('dateRange', v)}
          options={[
            { value: '7', label: 'Last 7 days' },
            { value: '30', label: 'Last 30 days' },
            { value: '90', label: 'Last 90 days' },
            { value: '365', label: 'Last year' },
          ]}
        />
      </FormField>

      <FormField label="Pipeline (optional)">
        <SelectInput
          value={formData.pipelineId}
          onChange={v => handleChange('pipelineId', v)}
          placeholder="All pipelines"
          options={pipelines.map(p => ({ value: p.id, label: p.name }))}
        />
      </FormField>

      <label className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 cursor-pointer">
        <input
          type="checkbox"
          checked={formData.stageBreakdown}
          onChange={e => handleChange('stageBreakdown', e.target.checked)}
          className="w-4 h-4 rounded"
        />
        <span className="text-sm text-white">Include stage breakdown</span>
      </label>

      {report && (
        <div className="mt-4 p-4 bg-white/5 rounded-xl border border-white/10">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="p-3 bg-green-500/10 rounded-lg">
              <p className="text-xs text-gray-400">Total Revenue</p>
              <p className="text-2xl font-bold text-green-400">${report.total_revenue?.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-blue-500/10 rounded-lg">
              <p className="text-xs text-gray-400">Opportunities</p>
              <p className="text-2xl font-bold text-blue-400">{report.opportunity_count}</p>
            </div>
          </div>
          {report.stage_breakdown && (
            <div className="space-y-2">
              <p className="text-xs text-gray-400">By Stage</p>
              {report.stage_breakdown.map(stage => (
                <div key={stage.name} className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                  <span className="text-sm text-white">{stage.name}</span>
                  <span className="text-sm text-green-400">${stage.value?.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </WizardModal>
  );
}

// 14) Campaign Performance Report Wizard
export function CampaignReportWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [formData, setFormData] = useState({
    campaignId: initialData.campaignId || '',
    dateRange: initialData.dateRange || '30',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const generateReport = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/campaign-report`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          campaign_id: formData.campaignId || undefined,
          date_range_days: parseInt(formData.dateRange),
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        setReport(result.report);
        toast.success('Campaign report generated!');
        onSuccess?.(result);
      } else {
        toast.error(result.error || 'Failed to generate report');
      }
    } catch (error) {
      toast.error('Error generating report: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Campaign Performance"
      subtitle="Analyze campaign results"
      icon={BarChart3}
      iconColor="bg-cyan-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Close</Button>
            <Button onClick={generateReport} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-2" />}
              Generate Report
            </Button>
          </div>
        </>
      }
    >
      <FormField label="Campaign ID (optional)" hint="Leave empty for all campaigns">
        <TextInput
          value={formData.campaignId}
          onChange={v => handleChange('campaignId', v)}
          placeholder="Campaign ID"
        />
      </FormField>

      <FormField label="Date Range" required>
        <SelectInput
          value={formData.dateRange}
          onChange={v => handleChange('dateRange', v)}
          options={[
            { value: '7', label: 'Last 7 days' },
            { value: '30', label: 'Last 30 days' },
            { value: '90', label: 'Last 90 days' },
          ]}
        />
      </FormField>

      {report && (
        <div className="mt-4 p-4 bg-white/5 rounded-xl border border-white/10">
          <div className="grid grid-cols-3 gap-4">
            <div className="p-3 bg-blue-500/10 rounded-lg text-center">
              <p className="text-xs text-gray-400">Clicks</p>
              <p className="text-xl font-bold text-blue-400">{report.clicks?.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-green-500/10 rounded-lg text-center">
              <p className="text-xs text-gray-400">Replies</p>
              <p className="text-xl font-bold text-green-400">{report.replies?.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-purple-500/10 rounded-lg text-center">
              <p className="text-xs text-gray-400">Conversions</p>
              <p className="text-xl font-bold text-purple-400">{report.conversions?.toLocaleString()}</p>
            </div>
          </div>
        </div>
      )}
    </WizardModal>
  );
}

// ═══════════════════════════════════════════════════════════════
// L) CUSTOMIZATION / ADMIN TOOLS
// ═══════════════════════════════════════════════════════════════

// 15) Create Custom Field Wizard
export function CreateCustomFieldWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    objectType: initialData.objectType || 'contact',
    fieldLabel: initialData.fieldLabel || '',
    fieldType: initialData.fieldType || 'text',
    options: initialData.options || '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.fieldLabel.trim()) {
      toast.error('Field label is required');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/custom-field`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          object_type: formData.objectType,
          field_label: formData.fieldLabel,
          field_type: formData.fieldType,
          options: formData.fieldType === 'dropdown' ? formData.options.split('\n').filter(Boolean) : undefined,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`Custom field "${formData.fieldLabel}" created!`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to create custom field');
      }
    } catch (error) {
      toast.error('Error creating custom field: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Custom Field"
      subtitle="Add a custom field to CRM"
      icon={Palette}
      iconColor="bg-gray-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Palette className="w-4 h-4 mr-2" />}
              Create Field
            </Button>
          </div>
        </>
      }
    >
      <FormField label="Object Type" required>
        <SelectInput
          value={formData.objectType}
          onChange={v => handleChange('objectType', v)}
          options={[
            { value: 'contact', label: 'Contact' },
            { value: 'opportunity', label: 'Opportunity' },
          ]}
        />
      </FormField>

      <FormField label="Field Label" required>
        <TextInput
          value={formData.fieldLabel}
          onChange={v => handleChange('fieldLabel', v)}
          placeholder="e.g., Preferred Contact Method"
        />
      </FormField>

      <FormField label="Field Type" required>
        <SelectInput
          value={formData.fieldType}
          onChange={v => handleChange('fieldType', v)}
          options={[
            { value: 'text', label: 'Text' },
            { value: 'number', label: 'Number' },
            { value: 'dropdown', label: 'Dropdown' },
            { value: 'date', label: 'Date' },
            { value: 'boolean', label: 'Yes/No' },
          ]}
        />
      </FormField>

      {formData.fieldType === 'dropdown' && (
        <FormField label="Options" required hint="One option per line">
          <TextArea
            value={formData.options}
            onChange={v => handleChange('options', v)}
            placeholder="Option 1&#10;Option 2&#10;Option 3"
            rows={4}
          />
        </FormField>
      )}
    </WizardModal>
  );
}

// 16) Create Tag Wizard
export function CreateTagWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    tagName: initialData.tagName || '',
    color: initialData.color || '#4F46E5',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.tagName.trim()) {
      toast.error('Tag name is required');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/tag`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          tag_name: formData.tagName,
          color: formData.color,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`Tag "${formData.tagName}" created!`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to create tag');
      }
    } catch (error) {
      toast.error('Error creating tag: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const colors = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Tag"
      subtitle="Create a new tag"
      icon={Tag}
      iconColor="bg-gray-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Tag className="w-4 h-4 mr-2" />}
              Create Tag
            </Button>
          </div>
        </>
      }
    >
      <FormField label="Tag Name" required>
        <TextInput
          icon={Tag}
          value={formData.tagName}
          onChange={v => handleChange('tagName', v)}
          placeholder="e.g., VIP Customer"
        />
      </FormField>

      <FormField label="Color">
        <div className="flex gap-2 flex-wrap">
          {colors.map(color => (
            <button
              key={color}
              type="button"
              onClick={() => handleChange('color', color)}
              className={cn(
                "w-8 h-8 rounded-lg transition-transform",
                formData.color === color && "ring-2 ring-white ring-offset-2 ring-offset-[#12121a] scale-110"
              )}
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
      </FormField>

      <div className="p-3 bg-white/5 rounded-xl border border-white/10">
        <p className="text-xs text-gray-400 mb-2">Preview</p>
        <span 
          className="px-3 py-1 rounded-full text-sm text-white"
          style={{ backgroundColor: formData.color }}
        >
          {formData.tagName || 'Tag Name'}
        </span>
      </div>
    </WizardModal>
  );
}

// 17) Create User Wizard
export function CreateUserWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: initialData.name || '',
    email: initialData.email || '',
    role: initialData.role || 'user',
    assignedPhone: initialData.assignedPhone || '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!formData.email.trim()) {
      toast.error('Email is required');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/user`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          role: formData.role,
          assigned_phone: formData.assignedPhone || undefined,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`User "${formData.name}" created!`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to create user');
      }
    } catch (error) {
      toast.error('Error creating user: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Create User"
      subtitle="Add a new team member"
      icon={UserPlus}
      iconColor="bg-gray-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Create User
            </Button>
          </div>
        </>
      }
    >
      <FormField label="Name" required>
        <TextInput
          icon={User}
          value={formData.name}
          onChange={v => handleChange('name', v)}
          placeholder="John Smith"
        />
      </FormField>

      <FormField label="Email" required>
        <TextInput
          icon={Mail}
          type="email"
          value={formData.email}
          onChange={v => handleChange('email', v)}
          placeholder="john@company.com"
        />
      </FormField>

      <FormField label="Role" required>
        <SelectInput
          value={formData.role}
          onChange={v => handleChange('role', v)}
          options={[
            { value: 'admin', label: 'Admin' },
            { value: 'user', label: 'User' },
            { value: 'viewer', label: 'Viewer' },
          ]}
        />
      </FormField>

      <FormField label="Assigned Phone Number (optional)">
        <TextInput
          icon={Phone}
          type="tel"
          value={formData.assignedPhone}
          onChange={v => handleChange('assignedPhone', v)}
          placeholder="(555) 123-4567"
        />
      </FormField>
    </WizardModal>
  );
}

// 18) Create Pipeline Wizard
export function CreatePipelineWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    pipelineName: initialData.pipelineName || '',
    stages: initialData.stages || [
      { name: 'New Lead', probability: 10 },
      { name: 'Qualified', probability: 25 },
      { name: 'Proposal', probability: 50 },
      { name: 'Negotiation', probability: 75 },
      { name: 'Closed Won', probability: 100 },
    ],
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateStage = (index, field, value) => {
    const newStages = [...formData.stages];
    newStages[index] = { ...newStages[index], [field]: value };
    handleChange('stages', newStages);
  };

  const addStage = () => {
    handleChange('stages', [...formData.stages, { name: '', probability: 50 }]);
  };

  const removeStage = (index) => {
    handleChange('stages', formData.stages.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!formData.pipelineName.trim()) {
      toast.error('Pipeline name is required');
      return;
    }
    if (formData.stages.length === 0) {
      toast.error('At least one stage is required');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/pipeline`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          pipeline_name: formData.pipelineName,
          stages: formData.stages,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`Pipeline "${formData.pipelineName}" created!`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to create pipeline');
      }
    } catch (error) {
      toast.error('Error creating pipeline: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Pipeline"
      subtitle="Build a new sales pipeline"
      icon={GitBranch}
      iconColor="bg-gray-500/20"
      size="large"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <GitBranch className="w-4 h-4 mr-2" />}
              Create Pipeline
            </Button>
          </div>
        </>
      }
    >
      <FormField label="Pipeline Name" required>
        <TextInput
          value={formData.pipelineName}
          onChange={v => handleChange('pipelineName', v)}
          placeholder="e.g., Sales Pipeline"
        />
      </FormField>

      <FormField label="Stages">
        <div className="space-y-2">
          {formData.stages.map((stage, index) => (
            <div key={index} className="flex gap-2 items-center">
              <span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs text-gray-400">
                {index + 1}
              </span>
              <TextInput
                value={stage.name}
                onChange={v => updateStage(index, 'name', v)}
                placeholder="Stage name"
              />
              <div className="w-24">
                <TextInput
                  type="number"
                  value={stage.probability}
                  onChange={v => updateStage(index, 'probability', parseInt(v) || 0)}
                  placeholder="%"
                />
              </div>
              <button
                type="button"
                onClick={() => removeStage(index)}
                className="p-2 hover:bg-red-500/20 rounded-lg text-red-400"
              >
                <Settings className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addStage}
            className="w-full py-2 border border-dashed border-white/20 rounded-xl text-sm text-gray-400 hover:bg-white/5"
          >
            + Add Stage
          </button>
        </div>
      </FormField>
    </WizardModal>
  );
}

// ═══════════════════════════════════════════════════════════════
// M) BULK ACTIONS
// ═══════════════════════════════════════════════════════════════

// 19) Bulk SMS Wizard
export function BulkSMSWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [recipientCount, setRecipientCount] = useState(0);
  const [formData, setFormData] = useState({
    filterType: initialData.filterType || 'tag',
    filterValue: initialData.filterValue || '',
    messageBody: initialData.messageBody || '',
    scheduleTime: initialData.scheduleTime || '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const previewRecipients = async () => {
    if (!formData.filterValue) return;
    
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/bulk-preview?filter_type=${formData.filterType}&filter_value=${formData.filterValue}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setRecipientCount(data.count || 0);
      }
    } catch (error) {
      console.error('Error previewing recipients:', error);
    }
  };

  useEffect(() => {
    if (formData.filterValue) {
      previewRecipients();
    }
  }, [formData.filterValue, formData.filterType]);

  const handleSubmit = async () => {
    if (!formData.filterValue) {
      toast.error('Please specify filter criteria');
      return;
    }
    if (!formData.messageBody.trim()) {
      toast.error('Message is required');
      return;
    }

    if (!showConfirmation) {
      setShowConfirmation(true);
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/bulk-sms`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          filter_type: formData.filterType,
          filter_value: formData.filterValue,
          message_body: formData.messageBody,
          schedule_time: formData.scheduleTime || undefined,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`Bulk SMS ${formData.scheduleTime ? 'scheduled' : 'sent'} to ${recipientCount} contacts!`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to send bulk SMS');
      }
    } catch (error) {
      toast.error('Error sending bulk SMS: ' + error.message);
    } finally {
      setSubmitting(false);
      setShowConfirmation(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Bulk SMS"
      subtitle="Send SMS to multiple contacts"
      icon={MessageSquare}
      iconColor="bg-red-500/20"
      footer={
        <>
          <div className="text-sm">
            <span className="text-gray-400">Recipients: </span>
            <span className={cn("font-semibold", recipientCount > 100 ? "text-red-400" : "text-white")}>
              {recipientCount}
            </span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} variant="destructive">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
              {showConfirmation ? 'Confirm Send' : 'Send Bulk SMS'}
            </Button>
          </div>
        </>
      }
    >
      {showConfirmation && (
        <ConfirmationBanner
          message={`You are about to send SMS to ${recipientCount} contacts. This action cannot be undone.`}
          onConfirm={handleSubmit}
          onCancel={() => setShowConfirmation(false)}
          loading={submitting}
        />
      )}

      <FormField label="Filter By" required>
        <SelectInput
          value={formData.filterType}
          onChange={v => handleChange('filterType', v)}
          options={[
            { value: 'tag', label: 'Tag' },
            { value: 'pipeline_stage', label: 'Pipeline Stage' },
            { value: 'manual_list', label: 'Manual List' },
          ]}
        />
      </FormField>

      <FormField label="Filter Value" required>
        <TextInput
          icon={Filter}
          value={formData.filterValue}
          onChange={v => handleChange('filterValue', v)}
          placeholder={formData.filterType === 'tag' ? 'Tag name' : 'Stage ID or contact IDs (comma separated)'}
        />
      </FormField>

      <FormField label="Message" required>
        <TextArea
          value={formData.messageBody}
          onChange={v => handleChange('messageBody', v)}
          placeholder="Hi {{contact.first_name}}, ..."
          rows={4}
          maxLength={1600}
        />
      </FormField>

      <FormField label="Schedule (optional)">
        <TextInput
          type="datetime-local"
          value={formData.scheduleTime}
          onChange={v => handleChange('scheduleTime', v)}
        />
      </FormField>

      <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400" />
          <p className="text-sm text-amber-200">Bulk actions require confirmation and are logged</p>
        </div>
      </div>
    </WizardModal>
  );
}

// 20) Bulk Tag Update Wizard
export function BulkTagWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [recipientCount, setRecipientCount] = useState(0);
  const [formData, setFormData] = useState({
    filterType: initialData.filterType || 'tag',
    filterValue: initialData.filterValue || '',
    action: initialData.action || 'add',
    tagName: initialData.tagName || '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.filterValue) {
      toast.error('Please specify filter criteria');
      return;
    }
    if (!formData.tagName.trim()) {
      toast.error('Tag name is required');
      return;
    }

    if (!showConfirmation) {
      setShowConfirmation(true);
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/crm/bulk-tag`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          filter_type: formData.filterType,
          filter_value: formData.filterValue,
          action: formData.action,
          tag_name: formData.tagName,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`Tag "${formData.tagName}" ${formData.action === 'add' ? 'added to' : 'removed from'} contacts!`);
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to update tags');
      }
    } catch (error) {
      toast.error('Error updating tags: ' + error.message);
    } finally {
      setSubmitting(false);
      setShowConfirmation(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Bulk Tag Update"
      subtitle="Add or remove tags in bulk"
      icon={Tag}
      iconColor="bg-red-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} variant="destructive">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Tag className="w-4 h-4 mr-2" />}
              {showConfirmation ? 'Confirm' : `${formData.action === 'add' ? 'Add' : 'Remove'} Tag`}
            </Button>
          </div>
        </>
      }
    >
      {showConfirmation && (
        <ConfirmationBanner
          message={`You are about to ${formData.action === 'add' ? 'add' : 'remove'} tag "${formData.tagName}" ${formData.action === 'add' ? 'to' : 'from'} matching contacts. This action cannot be undone.`}
          onConfirm={handleSubmit}
          onCancel={() => setShowConfirmation(false)}
          loading={submitting}
        />
      )}

      <FormField label="Action" required>
        <div className="flex gap-3">
          <label className={cn(
            "flex-1 p-3 rounded-xl border cursor-pointer transition-colors text-center",
            formData.action === 'add' 
              ? "bg-green-500/20 border-green-500/50 text-green-400" 
              : "bg-white/5 border-white/10 text-gray-400"
          )}>
            <input
              type="radio"
              name="action"
              value="add"
              checked={formData.action === 'add'}
              onChange={() => handleChange('action', 'add')}
              className="sr-only"
            />
            <span className="text-sm">Add Tag</span>
          </label>
          <label className={cn(
            "flex-1 p-3 rounded-xl border cursor-pointer transition-colors text-center",
            formData.action === 'remove' 
              ? "bg-red-500/20 border-red-500/50 text-red-400" 
              : "bg-white/5 border-white/10 text-gray-400"
          )}>
            <input
              type="radio"
              name="action"
              value="remove"
              checked={formData.action === 'remove'}
              onChange={() => handleChange('action', 'remove')}
              className="sr-only"
            />
            <span className="text-sm">Remove Tag</span>
          </label>
        </div>
      </FormField>

      <FormField label="Filter Criteria" required>
        <SelectInput
          value={formData.filterType}
          onChange={v => handleChange('filterType', v)}
          options={[
            { value: 'tag', label: 'Has Tag' },
            { value: 'pipeline_stage', label: 'Pipeline Stage' },
          ]}
        />
      </FormField>

      <FormField label="Filter Value" required>
        <TextInput
          icon={Filter}
          value={formData.filterValue}
          onChange={v => handleChange('filterValue', v)}
          placeholder="Filter value"
        />
      </FormField>

      <FormField label="Tag to Add/Remove" required>
        <TextInput
          icon={Tag}
          value={formData.tagName}
          onChange={v => handleChange('tagName', v)}
          placeholder="Tag name"
        />
      </FormField>
    </WizardModal>
  );
}

// ═══════════════════════════════════════════════════════════════
// ADDITIONAL POD & DIRECTORY WIZARDS
// ═══════════════════════════════════════════════════════════════

// Order Status/Escalation Wizard
export function OrderStatusWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [formData, setFormData] = useState({
    action: initialData.action || 'status',
    escalationReason: initialData.escalationReason || '',
  });

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await fetch(`${API_URL}/api/orders?status=in_progress`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setOrders(data || []);
        }
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoading(false);
      }
    };
    if (isOpen) fetchOrders();
  }, [isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!selectedOrder) {
      toast.error('Please select an order');
      return;
    }

    if (formData.action === 'escalate' && !formData.escalationReason.trim()) {
      toast.error('Escalation reason is required');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/pod/order-status`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          order_id: selectedOrder.id,
          action: formData.action,
          escalation_reason: formData.action === 'escalate' ? formData.escalationReason : undefined,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(formData.action === 'escalate' ? 'Order escalated!' : 'Status retrieved');
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to process request');
      }
    } catch (error) {
      toast.error('Error: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Order Status/Escalation"
      subtitle="Check or escalate an order"
      icon={Clock}
      iconColor="bg-purple-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting || loading}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Clock className="w-4 h-4 mr-2" />}
              {formData.action === 'escalate' ? 'Escalate' : 'Check Status'}
            </Button>
          </div>
        </>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : (
        <>
          <FormField label="Select Order" required>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {orders.map(order => (
                <button
                  key={order.id}
                  type="button"
                  onClick={() => setSelectedOrder(order)}
                  className={cn(
                    "w-full flex items-center gap-3 p-3 rounded-xl border transition-colors text-left",
                    selectedOrder?.id === order.id 
                      ? "bg-purple-500/10 border-purple-500/30" 
                      : "bg-white/5 border-white/10 hover:bg-white/10"
                  )}
                >
                  <Package className="w-4 h-4 text-gray-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{order.order_number} - {order.summary}</p>
                    <p className="text-xs text-gray-500">{order.status}</p>
                  </div>
                </button>
              ))}
            </div>
          </FormField>

          <FormField label="Action" required>
            <div className="flex gap-3">
              <label className={cn(
                "flex-1 p-3 rounded-xl border cursor-pointer transition-colors text-center",
                formData.action === 'status' 
                  ? "bg-blue-500/20 border-blue-500/50 text-blue-400" 
                  : "bg-white/5 border-white/10 text-gray-400"
              )}>
                <input
                  type="radio"
                  name="action"
                  value="status"
                  checked={formData.action === 'status'}
                  onChange={() => handleChange('action', 'status')}
                  className="sr-only"
                />
                <span className="text-sm">Check Status</span>
              </label>
              <label className={cn(
                "flex-1 p-3 rounded-xl border cursor-pointer transition-colors text-center",
                formData.action === 'escalate' 
                  ? "bg-red-500/20 border-red-500/50 text-red-400" 
                  : "bg-white/5 border-white/10 text-gray-400"
              )}>
                <input
                  type="radio"
                  name="action"
                  value="escalate"
                  checked={formData.action === 'escalate'}
                  onChange={() => handleChange('action', 'escalate')}
                  className="sr-only"
                />
                <span className="text-sm">Escalate</span>
              </label>
            </div>
          </FormField>

          {formData.action === 'escalate' && (
            <FormField label="Escalation Reason" required>
              <TextArea
                value={formData.escalationReason}
                onChange={v => handleChange('escalationReason', v)}
                placeholder="Describe why this order needs escalation..."
                rows={3}
              />
            </FormField>
          )}
        </>
      )}
    </WizardModal>
  );
}

// Asset Generator Wizard
export function AssetGeneratorWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    assetType: initialData.assetType || 'social_post',
    brandName: initialData.brandName || '',
    description: initialData.description || '',
    style: initialData.style || 'professional',
    format: initialData.format || '1080x1080',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.brandName.trim()) {
      toast.error('Brand name is required');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Description is required');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/pod/asset-generator`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          asset_type: formData.assetType,
          brand_name: formData.brandName,
          description: formData.description,
          style: formData.style,
          format: formData.format,
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success('Asset generation started!');
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to generate asset');
      }
    } catch (error) {
      toast.error('Error: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Asset Generator"
      subtitle="Generate marketing assets"
      icon={Zap}
      iconColor="bg-purple-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
              Generate
            </Button>
          </div>
        </>
      }
    >
      <FormField label="Asset Type" required>
        <SelectInput
          value={formData.assetType}
          onChange={v => handleChange('assetType', v)}
          options={[
            { value: 'social_post', label: 'Social Media Post' },
            { value: 'banner', label: 'Web Banner' },
            { value: 'logo', label: 'Logo' },
            { value: 'flyer', label: 'Flyer' },
            { value: 'email_header', label: 'Email Header' },
          ]}
        />
      </FormField>

      <FormField label="Brand Name" required>
        <TextInput
          value={formData.brandName}
          onChange={v => handleChange('brandName', v)}
          placeholder="Your brand name"
        />
      </FormField>

      <FormField label="Description" required hint="Describe what you want in the asset">
        <TextArea
          value={formData.description}
          onChange={v => handleChange('description', v)}
          placeholder="e.g., A promotional banner for our summer sale featuring..."
          rows={3}
        />
      </FormField>

      <div className="grid grid-cols-2 gap-4">
        <FormField label="Style">
          <SelectInput
            value={formData.style}
            onChange={v => handleChange('style', v)}
            options={[
              { value: 'professional', label: 'Professional' },
              { value: 'playful', label: 'Playful' },
              { value: 'minimalist', label: 'Minimalist' },
              { value: 'bold', label: 'Bold' },
            ]}
          />
        </FormField>
        <FormField label="Format">
          <SelectInput
            value={formData.format}
            onChange={v => handleChange('format', v)}
            options={[
              { value: '1080x1080', label: 'Square (1080x1080)' },
              { value: '1920x1080', label: 'Landscape (1920x1080)' },
              { value: '1080x1920', label: 'Portrait (1080x1920)' },
            ]}
          />
        </FormField>
      </div>
    </WizardModal>
  );
}

// Update Listing Wizard
export function UpdateListingWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [listings, setListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    phone: '',
    website: '',
  });

  useEffect(() => {
    const fetchListings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/directory/my-businesses`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setListings(data || []);
        }
      } catch (error) {
        console.error('Error fetching listings:', error);
      } finally {
        setLoading(false);
      }
    };
    if (isOpen) fetchListings();
  }, [isOpen]);

  const handleSelectListing = (listing) => {
    setSelectedListing(listing);
    setFormData({
      name: listing.name || '',
      description: listing.description || '',
      phone: listing.phone || '',
      website: listing.website || '',
    });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!selectedListing) {
      toast.error('Please select a listing');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/directory/businesses/${selectedListing.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          phone: formData.phone,
          website: formData.website,
        })
      });

      const result = await response.json();
      
      if (response.ok) {
        toast.success('Listing updated!');
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to update listing');
      }
    } catch (error) {
      toast.error('Error: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Update Listing"
      subtitle="Update an existing business listing"
      icon={Building2}
      iconColor="bg-green-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting || loading}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Building2 className="w-4 h-4 mr-2" />}
              Update Listing
            </Button>
          </div>
        </>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : (
        <>
          <FormField label="Select Listing" required>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {listings.map(listing => (
                <button
                  key={listing.id}
                  type="button"
                  onClick={() => handleSelectListing(listing)}
                  className={cn(
                    "w-full flex items-center gap-3 p-3 rounded-xl border transition-colors text-left",
                    selectedListing?.id === listing.id 
                      ? "bg-green-500/10 border-green-500/30" 
                      : "bg-white/5 border-white/10 hover:bg-white/10"
                  )}
                >
                  <Building2 className="w-4 h-4 text-gray-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{listing.name}</p>
                    <p className="text-xs text-gray-500">{listing.category}</p>
                  </div>
                </button>
              ))}
            </div>
          </FormField>

          {selectedListing && (
            <>
              <FormField label="Business Name">
                <TextInput
                  value={formData.name}
                  onChange={v => handleChange('name', v)}
                  placeholder="Business name"
                />
              </FormField>

              <FormField label="Description">
                <TextArea
                  value={formData.description}
                  onChange={v => handleChange('description', v)}
                  placeholder="Business description"
                  rows={3}
                />
              </FormField>

              <div className="grid grid-cols-2 gap-4">
                <FormField label="Phone">
                  <TextInput
                    icon={Phone}
                    value={formData.phone}
                    onChange={v => handleChange('phone', v)}
                    placeholder="(555) 123-4567"
                  />
                </FormField>
                <FormField label="Website">
                  <TextInput
                    icon={Globe}
                    value={formData.website}
                    onChange={v => handleChange('website', v)}
                    placeholder="https://..."
                  />
                </FormField>
              </div>
            </>
          )}
        </>
      )}
    </WizardModal>
  );
}

// Add Offer Wizard
export function AddOfferWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [listings, setListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [formData, setFormData] = useState({
    title: initialData.title || '',
    description: initialData.description || '',
    discountType: initialData.discountType || 'percentage',
    discountValue: initialData.discountValue || '',
    code: initialData.code || '',
    expiresAt: initialData.expiresAt || '',
    terms: initialData.terms || '',
  });

  useEffect(() => {
    const fetchListings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/directory/my-businesses`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setListings(data || []);
        }
      } catch (error) {
        console.error('Error fetching listings:', error);
      } finally {
        setLoading(false);
      }
    };
    if (isOpen) fetchListings();
  }, [isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!selectedListing) {
      toast.error('Please select a business');
      return;
    }
    if (!formData.title.trim()) {
      toast.error('Offer title is required');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/directory/coupons`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          business_id: selectedListing.id,
          title: formData.title,
          description: formData.description,
          discount_type: formData.discountType,
          discount_value: parseFloat(formData.discountValue) || 0,
          code: formData.code || undefined,
          expires_at: formData.expiresAt || undefined,
          terms: formData.terms || undefined,
        })
      });

      const result = await response.json();
      
      if (response.ok) {
        toast.success('Offer created!');
        onSuccess?.(result);
        onClose();
      } else {
        toast.error(result.error || 'Failed to create offer');
      }
    } catch (error) {
      toast.error('Error: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Offer/Lead Magnet"
      subtitle="Create a special offer for your listing"
      icon={Tag}
      iconColor="bg-green-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting || loading}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Tag className="w-4 h-4 mr-2" />}
              Create Offer
            </Button>
          </div>
        </>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : (
        <>
          <FormField label="Select Business" required>
            <SelectInput
              value={selectedListing?.id || ''}
              onChange={v => setSelectedListing(listings.find(l => l.id === v))}
              placeholder="Select a business"
              options={listings.map(l => ({ value: l.id, label: l.name }))}
            />
          </FormField>

          <FormField label="Offer Title" required>
            <TextInput
              value={formData.title}
              onChange={v => handleChange('title', v)}
              placeholder="e.g., 20% Off First Purchase"
            />
          </FormField>

          <FormField label="Description">
            <TextArea
              value={formData.description}
              onChange={v => handleChange('description', v)}
              placeholder="Describe the offer..."
              rows={2}
            />
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Discount Type">
              <SelectInput
                value={formData.discountType}
                onChange={v => handleChange('discountType', v)}
                options={[
                  { value: 'percentage', label: 'Percentage' },
                  { value: 'fixed', label: 'Fixed Amount' },
                  { value: 'bogo', label: 'Buy One Get One' },
                ]}
              />
            </FormField>
            <FormField label="Discount Value">
              <TextInput
                type="number"
                value={formData.discountValue}
                onChange={v => handleChange('discountValue', v)}
                placeholder={formData.discountType === 'percentage' ? '%' : '$'}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Promo Code (optional)">
              <TextInput
                value={formData.code}
                onChange={v => handleChange('code', v)}
                placeholder="SAVE20"
              />
            </FormField>
            <FormField label="Expires">
              <TextInput
                type="date"
                value={formData.expiresAt}
                onChange={v => handleChange('expiresAt', v)}
              />
            </FormField>
          </div>

          <FormField label="Terms & Conditions">
            <TextArea
              value={formData.terms}
              onChange={v => handleChange('terms', v)}
              placeholder="Any terms or restrictions..."
              rows={2}
            />
          </FormField>
        </>
      )}
    </WizardModal>
  );
}

// Listing Performance Report Wizard
export function ListingReportWizard({ isOpen, onClose, onSuccess, initialData = {}, conversationId }) {
  const [loading, setLoading] = useState(true);
  const [listings, setListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [report, setReport] = useState(null);
  const [formData, setFormData] = useState({
    dateRange: initialData.dateRange || '30',
  });

  useEffect(() => {
    const fetchListings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/directory/my-businesses`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setListings(data || []);
        }
      } catch (error) {
        console.error('Error fetching listings:', error);
      } finally {
        setLoading(false);
      }
    };
    if (isOpen) fetchListings();
  }, [isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const generateReport = async () => {
    if (!selectedListing) {
      toast.error('Please select a listing');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/wizards/directory/listing-report`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          listing_id: selectedListing.id,
          date_range_days: parseInt(formData.dateRange),
          conversation_id: conversationId
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        setReport(result.report);
        toast.success('Report generated!');
        onSuccess?.(result);
      } else {
        toast.error(result.error || 'Failed to generate report');
      }
    } catch (error) {
      toast.error('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <WizardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Listing Performance Report"
      subtitle="View analytics for your listing"
      icon={TrendingUp}
      iconColor="bg-green-500/20"
      footer={
        <>
          <div />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Close</Button>
            <Button onClick={generateReport} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TrendingUp className="w-4 h-4 mr-2" />}
              Generate Report
            </Button>
          </div>
        </>
      }
    >
      <FormField label="Select Listing" required>
        <SelectInput
          value={selectedListing?.id || ''}
          onChange={v => setSelectedListing(listings.find(l => l.id === v))}
          placeholder="Select a listing"
          options={listings.map(l => ({ value: l.id, label: l.name }))}
        />
      </FormField>

      <FormField label="Date Range">
        <SelectInput
          value={formData.dateRange}
          onChange={v => handleChange('dateRange', v)}
          options={[
            { value: '7', label: 'Last 7 days' },
            { value: '30', label: 'Last 30 days' },
            { value: '90', label: 'Last 90 days' },
          ]}
        />
      </FormField>

      {report && (
        <div className="mt-4 p-4 bg-white/5 rounded-xl border border-white/10">
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="p-3 bg-blue-500/10 rounded-lg text-center">
              <p className="text-xs text-gray-400">Views</p>
              <p className="text-2xl font-bold text-blue-400">{report.views?.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-green-500/10 rounded-lg text-center">
              <p className="text-xs text-gray-400">Clicks</p>
              <p className="text-2xl font-bold text-green-400">{report.clicks?.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-purple-500/10 rounded-lg text-center">
              <p className="text-xs text-gray-400">Leads</p>
              <p className="text-2xl font-bold text-purple-400">{report.leads?.toLocaleString()}</p>
            </div>
          </div>
          <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
            <span className="text-sm text-gray-400">Current Rating</span>
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
              <span className="text-white font-medium">{report.rating || 'N/A'}</span>
              <span className="text-gray-500 text-sm">({report.review_count || 0} reviews)</span>
            </div>
          </div>
        </div>
      )}
    </WizardModal>
  );
}
